'use client';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLayoutStore } from '@/hooks/use-layout-store';
import { useSidebarStore } from '@/hooks/use-sidebar-store';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo } from 'react';
import { CustomLayoutIcon } from '../../components/custom-layout-icon';
import {
    CustomMenuIconContent,
    CustomMenuLink,
} from '../../components/custom-menu';
import { CustomSidebarCounter } from '../../components/custom-sidebar-counter';
import {
    CustomerSidebarItemProps,
    CustomMenuDetailContentProps,
} from '../../types';

export const CustomSidebarItem: React.FC<CustomerSidebarItemProps> = memo(
    ({ className, ...props }) => {
        const isShowSidebarIconOnly = useSidebarStore(
            (state) => state.isShowSidebarIconOnly
        );

        const isDesktop = props.isDesktop ?? false;

        const Component =
            isShowSidebarIconOnly && isDesktop
                ? CustomMenuIconContent
                : CustomMenuDetailContent;

        return (
            <div
                className={cn(
                    'group ml-1 flex w-auto select-none flex-col',
                    className
                )}
            >
                <Component {...props} />
            </div>
        );
    }
);
CustomSidebarItem.displayName = 'CustomSidebarItem';

const CustomMenuDetailContent = ({
    className,
    item,
    pathname,
    isDesktop,
    isSearch,
}: CustomMenuDetailContentProps) => {
    const { objActive, setObjActive, setIsShowMenuOverlay } = useSidebarStore(
        (state) => state
    );

    const transSidebar = useTranslations('Sidebar');

    const setLoadingPage = useLayoutStore((x) => x.setLoadingPage);

    const handleClickMenuItem = () => {
        if (item.children && item.children.length > 0) {
            setObjActive((state) => {
                let newState = { ...state };
                newState[item.key] = !(newState[item.key] ?? false);
                return newState;
            });
        } else {
            setIsShowMenuOverlay((st) => (st ? false : st));
            if (item.href !== pathname) {
                // Trigger loading page
                setLoadingPage(true);
            }
        }
    };

    /**TODO: cắt sẵn và lưu thẳng vào item */
    const nodeLevel = item.key?.split('_')?.length ?? 1;
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = objActive[item.key] ?? false;
    const isCurrent = item.href === pathname;
    const isChild = nodeLevel > 1;

    // Custom styles logic for margin
    const childMarginLeft = !isChild || isSearch ? 2 : (nodeLevel - 1) * 18;

    const content = item.appLanguageResourceCode
        ? transSidebar(
              item.appLanguageResourceCode as AllSubKeysMessageResource<'Sidebar'>
          ) //TODO: type eror
        : item.name;

    return (
        <div className={cn('', className)}>
            <CustomMenuLink
                href={item.href}
                onClick={handleClickMenuItem}
                className={cn(
                    'group/hoolay relative flex h-10 w-full items-center gap-1.5 rounded-r-md transition-colors',
                    isCurrent
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-primary/30',
                    item.className
                )}
            >
                <div
                    style={{ marginLeft: childMarginLeft }}
                    className={cn('flex h-full items-center', {
                        'first:items-start': !isCurrent,
                        // 'items-start': hasChildren,
                    })}
                >
                    <div
                        className={cn(
                            'flex h-full w-2 border-l-4',
                            {
                                hidden: !isChild,
                                'group-hover/hoolay:h-3/4':
                                    !isCurrent && !hasChildren,
                                'border-secondary/25': isChild && !isCurrent,
                                'h-3/6 rounded-bl-md border-b-4': hasChildren,
                                'h-full': isOpen && hasChildren,
                                'border-primary/25': isOpen,
                                'border-primary-foreground': isCurrent,
                            },
                            isCurrent
                                ? 'h-2/6'
                                : 'group-hover/hoolay:border-primary/80'
                        )}
                    >
                        {hasChildren && isChild && isOpen && (
                            <div className='absolute top-[90%] ml-1 flex h-1/2 w-5 rounded-tr-lg border-r-4 border-t-4 border-primary' />
                        )}
                    </div>
                </div>

                {!isSearch && hasChildren && (
                    <ChevronRight
                        width={18}
                        height={18}
                        strokeWidth={3}
                        className={cn(
                            'cursor-pointer transition-transform duration-200 ease-in-out',
                            { 'rotate-90 text-primary': isOpen }
                        )}
                    />
                )}

                {item.icon && (
                    <CustomLayoutIcon
                        component={item.icon.component}
                        className={cn('h-5 w-5', item.icon.className, {
                            'text-primary-foreground': isCurrent,
                            'group-hover:transform-none': isChild,
                        })}
                    />
                )}

                <Tooltip
                    delayDuration={isDesktop ? 1500 : 500}
                    disableHoverableContent={!item.icon}
                >
                    <TooltipTrigger
                        className={cn('truncate text-left font-medium', {
                            'text-base font-bold': hasChildren,
                        })}
                    >
                        {content}
                    </TooltipTrigger>
                    <TooltipContent hideWhenDetached={!isDesktop}>
                        {content}
                    </TooltipContent>
                </Tooltip>

                <CustomSidebarCounter
                    item={item}
                    className={cn({
                        'bg-secondary/80 text-secondary-foreground': isCurrent,
                        'bg-primary/80 text-primary-foreground': !isCurrent,
                    })}
                />
            </CustomMenuLink>

            {hasChildren && isOpen && (
                <div
                    className={cn({ 'bg-secondary/15 pl-1.5 pt-1.5': isChild })}
                >
                    {item.children!.map((child, index) => (
                        <CustomMenuDetailContent
                            className={cn(className)}
                            item={child}
                            pathname={pathname}
                            isDesktop={isDesktop}
                            key={index}
                            isSearch={isSearch}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
